import React from 'react';
import { Clock, Minus, Plus, Shuffle } from 'lucide-react';

interface QuantitySelectorProps {
  quantity: number;
  onChange: (quantity: number) => void;
  minQuantity?: number;
  maxQuantity?: number;
}

const timePresets = [
  { label: '24 Hours', minutes: 1440 },
  { label: '7 Days', minutes: 10080 },
  { label: '1 Month', minutes: 43200 },
  { label: '1 Year', minutes: 525600 }
];

const formatNumber = (num: number) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatDuration = (minutes: number): string => {
  const years = Math.floor(minutes / 525600);
  const remainingAfterYears = minutes % 525600;

  const months = Math.floor(remainingAfterYears / 43200);
  const remainingAfterMonths = remainingAfterYears % 43200;

  const days = Math.floor(remainingAfterMonths / 1440);
  const remainingAfterDays = remainingAfterMonths % 1440;

  const hours = Math.floor(remainingAfterDays / 60);
  const remainingMinutes = remainingAfterDays % 60;

  const parts = [];

  if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
  if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
  if (remainingMinutes > 0) parts.push(`${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`);

  return parts.join(', ');
};

const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  quantity,
  onChange,
  minQuantity = 1440,
  maxQuantity = 525600
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value.replace(/,/g, ''));
    if (!isNaN(value)) {
      const clampedValue = Math.min(Math.max(value, minQuantity), 525600);
      onChange(clampedValue);
    }
  };

  const adjustQuantity = (amount: number) => {
    const newValue = quantity + amount;
    if (newValue >= minQuantity && newValue <= maxQuantity) {
      onChange(newValue);
    }
  };

  const generateRandomDuration = () => {
    const randomMinutes = Math.floor(Math.random() * (maxQuantity - minQuantity + 1)) + minQuantity;
    onChange(randomMinutes);
  };

  return (
    <div className="space-y-6" role="group" aria-labelledby="duration-label">
      <div id="duration-label" className="sr-only">Select duration</div>

      <div className="flex flex-wrap gap-4" role="group" aria-label="Duration presets">
        {timePresets.map((preset) => (
          <button
            key={preset.label}
            onClick={() => onChange(preset.minutes)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-dark-900 ${quantity === preset.minutes
              ? 'bg-primary-500 text-white'
              : 'bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white'
              }`}
            aria-pressed={quantity === preset.minutes}
          >
            <Clock size={16} aria-hidden="true" />
            <span>{preset.label}</span>
          </button>
        ))}
        <button
          onClick={generateRandomDuration}
          className="flex items-center px-4 py-2 space-x-2 rounded-md transition-colors bg-dark-800 text-dark-300 hover:bg-dark-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-dark-900"
          aria-label="Generate random duration"
        >
          <Shuffle size={16} aria-hidden="true" />
          <span>Random</span>
        </button>
      </div>

      <div className="space-y-2">
        <label htmlFor="duration-input" className="block text-sm font-medium text-dark-300">
          Custom Duration (minutes):
        </label>
        <div className="flex items-center space-x-4">
          <div className="flex items-center rounded-md bg-dark-800">
            <button
              type="button"
              onClick={() => adjustQuantity(-60)}
              aria-label="Decrease duration by 60 minutes"
              className="p-2 rounded-l-md transition-colors text-dark-400 hover:text-white hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-dark-900"
              disabled={quantity <= minQuantity}
            >
              <Minus size={16} aria-hidden="true" />
            </button>

            <input
              type="text"
              id="duration-input"
              value={formatNumber(quantity)}
              onChange={handleInputChange}
              className="w-32 text-center text-white bg-transparent border-none focus:ring-2 focus:ring-primary-400 focus:outline-none"
              aria-label="Duration in minutes"
              aria-valuemin={minQuantity}
              aria-valuemax={maxQuantity}
              aria-valuenow={quantity}
            />

            <button
              type="button"
              onClick={() => adjustQuantity(60)}
              aria-label="Increase duration by 60 minutes"
              className="p-2 rounded-r-md transition-colors text-dark-400 hover:text-white hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-dark-900"
              disabled={quantity >= maxQuantity}
            >
              <Plus size={16} aria-hidden="true" />
            </button>
          </div>
          <span className="text-dark-400" aria-label="Price per minute">€0.01 per minute</span>
        </div>
        <div className="text-sm text-dark-400" aria-live="polite">
          {formatDuration(quantity)}
        </div>
      </div>
    </div>
  );
};

export default QuantitySelector;